import type { Reference, RelatedPerson } from '@medplum/fhirtypes'
import {
  addDays,
  formatISO,
  isSunday,
  isWeekend,
  parseISO,
  setHours,
  setMinutes,
  subDays,
} from 'date-fns'

export type SDRRequesterState =
  | {
      type: 'participant'
    }
  | {
      type: 'relatedPerson'
      reference: Reference<RelatedPerson>
    }
  | {
      type: 'newRelatedPerson'
      firstName: string
      lastName: string
      phone: string
      email: string
      relationship: string | null

      streetAddress: string
      city: string
      state: string
      zip: string
      country: string
    }

export function isValidRequester(
  requester: SDRRequesterState | null
): requester is SDRRequesterState {
  if (requester?.type == null) {
    return false
  }

  switch (requester.type) {
    case 'participant':
    case 'relatedPerson':
      return true
    case 'newRelatedPerson':
      return (
        requester.firstName.trim().length > 0 &&
        requester.lastName.trim().length > 0 &&
        requester.phone.trim().length > 0 &&
        requester.email.trim().length > 0 &&
        requester.relationship != null &&
        requester.relationship.trim().length > 0
      )
  }
}

export const SDRDeadlineDays = {
  // 3 days after receipt
  IDTReview: 3,
  // 3 days after IDT review
  DecisionNotification: 3,
  // 7 days after decision notification
  Effectuation: 7,
}

export function adjustDateForSDRDeadlineAvoidingWeekends(
  receiptDate: string | Date,
  days: keyof typeof SDRDeadlineDays
): string {
  return adjustDateByDaysAvoidingWeekends(receiptDate, SDRDeadlineDays[days])
}

function adjustDateByDaysAvoidingWeekends(
  receiptDate: string | Date,
  days: number
): string {
  const receiptDateObj =
    receiptDate instanceof Date ? receiptDate : parseISO(receiptDate)

  // The due date is 3 calendar days from the reference date.
  // If this falls on a weekend, then the due date should be moved up to Friday.
  // https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-E/part-460/subpart-G/section-460.121
  let dueDate = addDays(receiptDateObj, days)
  dueDate = setMinutes(setHours(dueDate, 17), 0)

  // If Saturday or Sunday, move to Friday
  if (isWeekend(dueDate)) {
    dueDate = subDays(dueDate, isSunday(dueDate) ? 2 : 1)
  }

  // If it's a valid date, return the due date in ISO format
  return Number.isNaN(dueDate.getTime()) ? '' : formatISO(dueDate)
}
